<script setup lang="ts">
import { useDropdown } from "@/composables/useDropdown.ts";
import { useGlobalStore } from "@/store/globals.ts";
import { THEME, useThemeStore } from "@curasoft/frontend.core/other";
import { computed } from "vue";

type ThemeOption = {
  id: THEME;
  name: string;
  icon: string;
  color: string;
};

const themeOptions: ThemeOption[] = [
  {
    id: THEME.LIGHT,
    name: "Tag",
    icon: "sun",
    color: "#FFB700",
  },
  {
    id: THEME.DARK,
    name: "Nacht",
    icon: "moon",
    color: "#85A0CC",
  },
  {
    id: THEME.SYSTEM,
    name: "System",
    icon: "desktop",
    color: "#999999",
  },
];

const { hasDarkBackground = true } = defineProps<{
  hasDarkBackground?: boolean;
}>();

const themeStore = useThemeStore();
const currentTheme = computed(
  () =>
    themeOptions.find((theme) => theme.id === themeStore.current) ??
    themeOptions[2],
);
const { isOpen, modalRef, toggleDropdown } = useDropdown();
const globals = useGlobalStore();
</script>

<template>
  <div class="relative">
    <button
      id="themeDropdown"
      class="block items-center rounded-lg px-4 py-2 transition-colors
        duration-200"
      :class="{
        [`text-gray-100 hover:bg-cs-900-lighter ui-open:outline
        ui-open:outline-cs-300`]: globals.isDesktop,
        'focus:outline focus:outline-cs-300': globals.isDesktop && isOpen,
        [`bg-gray-100 text-black hover:bg-gray-200 dark:bg-cs-950
        dark:text-gray-100 dark:hover:bg-gray-700`]: !globals.isDesktop,
      }"
      @click="toggleDropdown"
    >
      <span
        class="inline pr-1.5 text-lg [@media(min-width:0960px)]:hidden
          [@media(min-width:1030px)]:inline"
        >Theme</span
      >
      <IconFA
        :icon="currentTheme.icon"
        class="size-[1.125rem]"
        :class="{
          'text-cs-100': hasDarkBackground,
          'text-gray-400': !hasDarkBackground,
        }"
      />
    </button>
    <Transition
      enter-active-class="transition-opacity duration-200 ease"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200 ease"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-show="isOpen"
        ref="modalRef"
        class="absolute -right-10 z-10 mt-1 flex w-48 flex-col rounded-lg border
          border-gray-200 bg-white text-gray-600 shadow-lg focus:outline-hidden
          dark:border-cs-950 dark:bg-cs-900 dark:text-gray-300"
        :class="{ '-top-32 -left-10': !globals.isDesktop }"
      >
        <button
          v-for="theme in themeOptions"
          :key="theme.id"
          class="group flex w-full items-center px-4 py-2 text-sm text-black
            transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg
            hover:bg-cs-200 dark:text-gray-300 dark:hover:bg-cs-900-lighter"
          :class="{
            'bg-cs-100 dark:bg-cs-600 dark:text-white':
              theme.id === themeStore.current,
          }"
          @click="themeStore.setTheme(theme.id)"
        >
          <IconFA
            :icon="theme.icon"
            class="mr-3 size-5 group-hover:text-gray-500
              dark:group-hover:text-gray-200"
            :style="{ color: theme.color }"
          />
          <span>{{ theme.name }}</span>
        </button>
      </div>
    </Transition>
  </div>
</template>
